import { useState, useCallback } from "react";
import {
  loadTableConfig,
  saveTableConfig,
} from "../Table/Internal/tableConfig";
import { notifyUrlParamsChanged } from "./useUrlParamsTick";

interface ColumnDef {
  key: string;
  defaultHidden?: boolean | undefined;
}

interface TableConfigState {
  hiddenKeys: Set<string>;
  columnOrder: string[];
  sortKey: string | undefined;
  sortDir: "asc" | "desc";
  setHiddenKeys: (
    updater: Set<string> | ((prev: Set<string>) => Set<string>),
  ) => void;
  setColumnOrder: (order: string[]) => void;
  setSort: (key: string | undefined, dir: "asc" | "desc") => void;
  saveAsDefault: () => void;
}

function readParam(key: string): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return params.get(key) ?? undefined;
}

function writeParam(key: string, value: string | undefined) {
  const url = new URL(window.location.href);
  if (value === undefined) {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, value);
  }
  window.history.replaceState({}, "", url.toString());
  // Column changes write the URL but only re-render the Table. Notify so an
  // ancestor deriving state from `?cols=` (e.g. a Saved View's dirty banner)
  // re-renders too.
  notifyUrlParamsChanged();
}

interface CompactConfig {
  h?: string[];
  s?: string[];
  o?: string[];
  sk?: string;
  sd?: "asc" | "desc";
}

function serializeConfig(
  hiddenKeys: Set<string>,
  columnOrder: string[],
  defaultHidden: Set<string>,
  defaultOrder: string[],
  sortKey: string | undefined,
  sortDir: "asc" | "desc",
): string | undefined {
  const config: CompactConfig = {};

  // h = extra hidden keys beyond defaults
  const extraHidden = Array.from(hiddenKeys)
    .filter((k) => !defaultHidden.has(k))
    .sort();
  // s = default-hidden keys that the user chose to show
  const shown = Array.from(defaultHidden)
    .filter((k) => !hiddenKeys.has(k))
    .sort();

  if (extraHidden.length) config.h = extraHidden;
  if (shown.length) config.s = shown;

  if (JSON.stringify(columnOrder) !== JSON.stringify(defaultOrder)) {
    config.o = columnOrder;
  }

  if (sortKey) {
    config.sk = sortKey;
    if (sortDir !== "asc") config.sd = sortDir;
  }

  if (!config.h && !config.s && !config.o && !config.sk) return undefined;
  return JSON.stringify(config);
}

function deserializeConfig(raw: string):
  | {
      hiddenKeys: string[];
      shownKeys: string[];
      columnOrder: string[];
      sortKey: string | undefined;
      sortDir: "asc" | "desc" | undefined;
    }
  | undefined {
  try {
    const parsed = JSON.parse(raw) as CompactConfig;
    return {
      hiddenKeys: parsed.h ?? [],
      shownKeys: parsed.s ?? [],
      columnOrder: parsed.o ?? [],
      sortKey: parsed.sk,
      sortDir: parsed.sd,
    };
  } catch {
    return undefined;
  }
}

function readUrlSort(
  validKeys: string[],
): { sortKey: string; sortDir: "asc" | "desc" | undefined } | undefined {
  const urlRaw = readParam("cols");
  if (!urlRaw) return undefined;
  const parsed = deserializeConfig(urlRaw);
  if (!parsed?.sortKey || !validKeys.includes(parsed.sortKey)) return undefined;
  return { sortKey: parsed.sortKey, sortDir: parsed.sortDir };
}

/**
 * The sort a configurable table will start with from `?cols=`, for a caller
 * that fetches before the table mounts (a server-paged table) and so must ask
 * for that sort in its first request instead of learning it a fetch later.
 */
export function readTableSortParam(
  columnKeys: string[],
): { sortKey: string; sortDir: "asc" | "desc" } | undefined {
  const fromUrl = readUrlSort(columnKeys);
  return (
    fromUrl && { sortKey: fromUrl.sortKey, sortDir: fromUrl.sortDir ?? "asc" }
  );
}

export function useTableConfigParam(
  tableId: string | undefined,
  columns: ColumnDef[],
  defaultSortKey?: string | undefined,
  defaultSortDir?: "asc" | "desc" | undefined,
  /** Only read/write URL params and localStorage when the table is configurable. */
  configurable?: boolean | undefined,
): TableConfigState {
  const defaultHidden = new Set(
    columns.filter((c) => c.defaultHidden).map((c) => c.key),
  );
  const defaultOrder = columns.map((c) => c.key);

  // oxlint-disable-next-line sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
  const [hiddenKeys, setHiddenKeysRaw] = useState<Set<string>>(() => {
    // Only read persisted config for configurable tables — non-configurable
    // tables should never be affected by URL params or localStorage.
    if (!configurable) return new Set(defaultHidden);

    // 1. URL param — h adds to defaults, s removes from defaults
    const urlRaw = readParam("cols");
    if (urlRaw) {
      const parsed = deserializeConfig(urlRaw);
      if (parsed) {
        const merged = new Set(defaultHidden);
        for (const key of parsed.hiddenKeys) merged.add(key);
        for (const key of parsed.shownKeys) merged.delete(key);
        return merged;
      }
    }

    // 2. localStorage — merge in defaultHidden for columns added after the config was saved
    if (tableId) {
      const saved = loadTableConfig(tableId);
      if (saved) {
        const knownKeys = new Set(
          saved.columnOrder.length ? saved.columnOrder : defaultOrder,
        );
        const merged = new Set(saved.hiddenKeys);
        for (const key of defaultHidden) {
          if (!knownKeys.has(key)) merged.add(key);
        }
        return merged;
      }
    }

    // 3. Defaults
    return new Set(defaultHidden);
  });

  const [columnOrder, setColumnOrderRaw] = useState<string[]>(() => {
    // 1. URL param
    const urlRaw = readParam("cols");
    if (urlRaw) {
      const parsed = deserializeConfig(urlRaw);
      if (parsed?.columnOrder.length) return parsed.columnOrder;
    }

    // 2. localStorage
    if (tableId) {
      const saved = loadTableConfig(tableId);
      if (saved?.columnOrder.length) return saved.columnOrder;
    }

    // 3. Defaults
    return defaultOrder;
  });

  // `?cols=` is one param shared by every table on the page, so a sort key
  // belonging to a sibling table can arrive here. Ignore anything this table
  // has no column for — otherwise the lookup in Table misses and the rows
  // render in raw order with no caret and no default sort.
  const [sortKey, setSortKeyRaw] = useState<string | undefined>(
    () => readUrlSort(defaultOrder)?.sortKey ?? defaultSortKey,
  );

  const [sortDir, setSortDirRaw] = useState<"asc" | "desc">(() => {
    const fromUrl = readUrlSort(defaultOrder);
    return (fromUrl ? fromUrl.sortDir : defaultSortDir) ?? "asc";
  });

  const syncUrl = useCallback(
    (
      nextHidden: Set<string>,
      nextOrder: string[],
      nextSortKey: string | undefined,
      nextSortDir: "asc" | "desc",
    ) => {
      const serialized = serializeConfig(
        nextHidden,
        nextOrder,
        defaultHidden,
        defaultOrder,
        nextSortKey,
        nextSortDir,
      );
      writeParam("cols", serialized);
    },
    [defaultHidden, defaultOrder],
  );

  const setHiddenKeys = useCallback(
    (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setHiddenKeysRaw((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        syncUrl(next, columnOrder, sortKey, sortDir);
        return next;
      });
    },
    [syncUrl, columnOrder, sortKey, sortDir],
  );

  const setColumnOrder = useCallback(
    (order: string[]) => {
      setColumnOrderRaw(order);
      syncUrl(hiddenKeys, order, sortKey, sortDir);
    },
    [syncUrl, hiddenKeys, sortKey, sortDir],
  );

  // Key and direction move together in one call. Setting them separately made
  // each write serialize the other's stale closure value, so the second write
  // persisted the previous column.
  const setSort = useCallback(
    (key: string | undefined, dir: "asc" | "desc") => {
      setSortKeyRaw(key);
      setSortDirRaw(dir);
      syncUrl(hiddenKeys, columnOrder, key, dir);
    },
    [syncUrl, hiddenKeys, columnOrder],
  );

  const saveAsDefault = useCallback(() => {
    if (!tableId) return;
    saveTableConfig(tableId, {
      hiddenKeys: Array.from(hiddenKeys),
      columnOrder,
    });
  }, [tableId, hiddenKeys, columnOrder]);

  return {
    hiddenKeys,
    columnOrder,
    sortKey,
    sortDir,
    setHiddenKeys,
    setColumnOrder,
    setSort,
    saveAsDefault,
  };
}
